import { Component, inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { combineLatest, Observable, Subject } from "rxjs";
import {
    filter,
    map,
    switchMap,
    take,
    tap
} from "rxjs/operators";
import { DigitalSignaturDeltagareTyper, DokumentTyp } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { DateUtils } from "~app/common/utils/date-utils";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { UtskriftTyp, VKKontraktUtskriftDTO } from "~app/features/virkeskop/models/utskrift/utskrift-models";
import { DigitalSigneringMediatorService } from "~app/features/virkeskop/services/digitalsignering-mediator.service";
import { VirkeskopValidationIconPopoverComponent } from "~app/features/virkeskop/validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { VirkeskopskontraktDigitalSigneringViewModel, VKDigitalsigneringUtskrifterDTO } from "../../../models/kontrakt-models";
import { IKontraktSigneringRequestDTO } from "../../../models/utskrift/utskriftSignering-models";
import { DigitalSigneringFormGroup } from "../digital-signernig-form";
import { KontraktSigneringLeverantorComponent } from "./kontrakt-signering-leverantor/kontrakt-signering-leverantor.component";
import { IVKDigitalsignaturDeltagare } from "./kontrakt-signering-model";
import { KontraktSigneringUtskriftComponent } from "./kontrakt-signering-utskrift/kontrakt-signering-utskrift.component";

@Component({
    selector: "rask-kontrakt-signering",
    templateUrl: "./kontrakt-signering.component.html",
    styleUrls: ["./kontrakt-signering.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RaskCommonModule,
        VirkeskopValidationIconPopoverComponent,
        KontraktSigneringUtskriftComponent,
        KontraktSigneringLeverantorComponent
    ]
})
export class KontraktSigneringComponent implements OnInit {
    modal = inject(NgbActiveModal);

    form: DigitalSigneringFormGroup;
    aktivSignering$: Observable<VirkeskopskontraktDigitalSigneringViewModel>;
    dokumentTyp: UtskriftTyp;
    protected kontraktUtskrifter$: Observable<VKDigitalsigneringUtskrifterDTO>;
    protected deltagareList$: Observable<IVKDigitalsignaturDeltagare[]>;

    private get antalDagar() {
        return this.form.controls.giltigAntalDagar.value;
    }

    get endDate() {
        return ObjectUtils.hasValue(this.antalDagar) ? DateUtils.shortDateString(DateUtils.nowAddDays(this.antalDagar)) : undefined;
    }

    private readonly saveSrc = new Subject<void>();

    private readonly digitalSigneringMediatorService = inject(DigitalSigneringMediatorService);
    ngOnInit(): void {
        this.kontraktUtskrifter$ = this.digitalSigneringMediatorService.getKontraktUtskrifter(this.dokumentTyp).pipe(
            tap(utskrifter => this.filterUtskrifterFormArray([
                ...utskrifter.utskrifter,
                ...utskrifter.ovrigaBilagor
            ]))
        );

        this.deltagareList$ = this.digitalSigneringMediatorService.deltagareList$;

        const save$ = combineLatest([
            this.kontraktUtskrifter$,
            this.deltagareList$
        ]).pipe(
            tap(([utskrifter]) => this.save(utskrifter))
        );

        this.saveSrc.pipe(
            filter(() => this.form.valid),
            switchMap(() => save$),
            take(1)
        ).subscribe();
    }

    formatter = (item: VKKontraktUtskriftDTO) => item.dokumentNamn;

    onOvrigBilagaChange(selectedKontraktUtskrift: VKKontraktUtskriftDTO) {
        const file = this.form.controls.utskrifter.controls.find(x => x.controls.id.value === selectedKontraktUtskrift?.id);
        file?.controls.selected.setValue(true);
        file?.enable();
    }

    addMottagare() {
        const nyExtradeltagare: IVKDigitalsignaturDeltagare = {
            isPersonnummer: true,
            deltagarTyp: DigitalSignaturDeltagareTyper.extraDeltagare,
            makeGodkannandeKravsEj: null,
            deltagarGuid: GuidUtils.newGuid(),
            inbjudningsOrdning: 1
        };
        this.digitalSigneringMediatorService.addMottagare(nyExtradeltagare);
    }

    correctOrder(): boolean {
        if (!this.form.touched) {
            return true;
        }
        const formValues = this.form.getRawValue();
        const signeringsOrdning = formValues.leverantorer.map(x => (x.inbjudningsOrdning));
        if (signeringsOrdning.includes(0)) {
            return false;
        }
        if (!signeringsOrdning.includes(1)) {
            return false;
        }
        return this.hasNoGaps(signeringsOrdning);
    }

    protected getOvrigaBilagor(utskriftInfo: VKDigitalsigneringUtskrifterDTO) {
        const disabledList = this.form.controls.utskrifter.controls.filter(x => x.disabled).map(p => p.controls.id.value);
        return utskriftInfo.ovrigaBilagor.filter(x => disabledList.includes(x.id));
    }

    protected canSave() {
        return false;
    }

    protected saveClick() {
        this.form.markAllAsTouched();
        if(!this.form.valid) {
            return;
        }
        this.saveSrc.next();
    }

    protected close() {
        this.modal.dismiss();
    }

    protected getErrors() {
        const errors: string[] = [];

        this.form.controls.leverantorer.controls.forEach(l => {
            if (l.controls.epost.hasError("required") && l.controls.deltagarTyp.value !== DigitalSignaturDeltagareTyper.extraDeltagare) {
                errors.push("En leverantör saknar epost");
            }
            if (l.controls.epost.hasError("required") && l.controls.deltagarTyp.value === DigitalSignaturDeltagareTyper.extraDeltagare) {
                errors.push("Epost saknas");
            }

            if (l.controls.makeMaka.controls.epost.hasError("email") || l.controls.makeMaka.controls.epost.hasError("pattern")) {
                errors.push("En make/maka har en felaktigt formatterad epost");
            }
            if (l.controls.makeMaka.controls.signeringsmetod.hasError("required")) {
                errors.push("Signeringsmetod måste anges");
            }
            if (l.controls.makeMaka.controls.smsNummer.hasError("nonValidPhonenumber")) {
                errors.push("Felaktigt formaterat mobilnummer för make/maka sms signering");
            }
            if (l.controls.signeringsmetod.hasError("required")) {
                errors.push("Signeringsmetod måste anges");
            }
            if (l.controls.smsNummer.hasError("nonValidPhonenumber")) {
                errors.push("Felaktigt mobilnummer för sms signering");
            }
            if (l.controls.makeMaka.controls.epost.hasError("required")) {
                errors.push("En make/maka saknar epost");
            }
        });

        if (this.form.controls.giltigAntalDagar.hasError("required")) {
            errors.push("Antal dagar måste anges");
        }

        if (this.form.controls.giltigAntalDagar.hasError("min")) {
            errors.push("Giltighetstid måste minst vara 1 dag");
        }
        return errors;
    }

    protected showMakeMakeSelection(): Observable<boolean> {
        return this.deltagareList$.pipe(
            map(leverantorer => leverantorer.some(leverantor => leverantor.isPersonnummer) && this.dokumentTyp === DokumentTyp.Kontrakt)
        );
    }

    private hasNoGaps(numbers: number[]): boolean {
        if (numbers.length === 0) {
            return true;
        }

        const uniqueSorted = [...new Set(numbers)].sort((a, b) => a - b);

        for (let i = 1; i < uniqueSorted.length; i++) {
            if (uniqueSorted[i] !== uniqueSorted[i - 1] + 1) {
                return false;
            }
        }
        return true;
    }

    private filterUtskrifterFormArray(utskrifter: VKKontraktUtskriftDTO[]) {
        const utskrifterFormArray = this.form.controls.utskrifter;
        const utskrifterIdList = utskrifter.map(u => u.id);
        utskrifterFormArray.controls = utskrifterFormArray.controls.filter(c => utskrifterIdList.includes(c.controls.id.value));
    }

    private save(utskrifter: VKDigitalsigneringUtskrifterDTO) {
        const formValues = this.form.getRawValue();
        const utvaldaUtskrifterIds = formValues.utskrifter
            .filter(u => u.selected)
            .map(u => u.id);
        const utvaldaLeverantorer = formValues.leverantorer.map(x => ({
            leverantorGuid: x.leverantorGuid,
            namn: x.namn,
            persOrgNummer: x.persOrgNummer,
            epost: x.epost,
            alternativKontaktperson: x.alternativKontaktperson,
            telefon: x.telefon,
            signeringsmetod: x.signeringsmetod,
            smsNummer: x.smsNummer,
            shouldSendMakeMaka: x.signeringMakeMaka,
            makeMakaEpost: x.makeMaka?.epost,
            makeMakaNamn: x.makeMaka?.namn,
            makeMakaSigneringsmetod: x.makeMaka?.signeringsmetod,
            makeMakaSmsNummer: x.makeMaka?.smsNummer,
            deltagarTyp: x.deltagarTyp,
            inbjudningsOrdning: x.inbjudningsOrdning
        }));
        const kontraktSignering: IKontraktSigneringRequestDTO = {
            utvaldaLeverantorer: utvaldaLeverantorer,
            utvaldaUtskrifter: [
                ...utskrifter.utskrifter,
                ...utskrifter.ovrigaBilagor
            ].filter(u => utvaldaUtskrifterIds.includes(u.id)),
            giltigAntalDagar: formValues.giltigAntalDagar
        };
        this.modal.close(kontraktSignering);
    }
}
